import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type MouseEvent } from 'react'
import {
  removeCatalogEntry,
  serviceCatalogQuery,
  type CatalogEntry,
} from '../api/serviceCatalog'
import { moduleOf, shellQuery } from '../api/shell'
import { esc } from '../cmdb/cells'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CatalogHead, CatalogTabs } from './ServiceCatalogChrome'
import { CompositionTab } from './ServiceCatalogCompositionPage'
import { CostsTab } from './ServiceCatalogCosts'
import { DependenciesTab } from './ServiceCatalogDependenciesPage'
import { ServiceCatalogForm } from './ServiceCatalogForm'

/**
 * Catalogul de servicii - `Service_catalog::index()`, macheta
 * `views/version-3/inventoree/service_catalog/index.php` si scriptul
 * `public/js/service_catalog.js`.
 *
 * Serviciile de business intai, cele de IT dupa - asta e ordinea in care le
 * citeste banca: ce vinde ea, apoi ce tine IT-ul ca sa fie posibil. Ordinea o
 * face SERVERUL, in aceeasi interogare ca in portal.
 *
 * ---------------------------------------------------------------------------
 * LISTA E O LISTA, CA ORICARE ALTA DIN MODUL
 * ---------------------------------------------------------------------------
 *
 * La mutare tabelul era scris de mana: un `<table class="dataTableM">` si atat.
 * Fara cautare, fara sortare, fara paginare si fara Copy/Columns/Refresh, iar
 * "Add" si "Filters" stateau intr-o banda verde-albastra deasupra tabelului -
 * adica exact ce a spus omul, "nu arata ca pe cec master".
 *
 * In portal ecranul NU e asa. `service_catalog.js` il porneste ca DataTable, cu
 * cincizeci de randuri pe pagina, cu `stateSave`, cu butoanele lui proprii
 * concatenate peste `DataTablesProperties.buttons`, si cu bara de filtre mutata
 * in `.userFilters`. Dupa aia trece `list_chrome.js`: cautarea si Filters urca
 * in antet, Copy/Columns/Refresh coboara in subsol, butonul verde se aseaza
 * langa cautare, si `body` primeste `tk-list-flush` - deci PAGINA INTREAGA se
 * lipeste de margini, cartonasul de titlu si banda de file cu tot.
 *
 * `DataTable` din v4 e chiar forma aia, deja asezata. Nu se schimba nimic din
 * ce SCRIE ecranul: aceleasi opt coloane, aceleasi pastile, acelasi rand
 * desfacut. Se schimba numai chromul din jurul lor, si marginea vine odata cu
 * el - aceeasi ca in portal, unde `tk-list-flush` e deja pusa de lista asta.
 *
 * FILTRELE MERG LA SERVER, ca in portal: catalogul poate creste la mii de
 * randuri, iar un drum la server costa mai putin decat o copie tinuta cinstita
 * in browser. Cautarea din antet e chiar `search`-ul serverului - acelasi camp
 * pe care il scria `#catSearch` din bara de filtre, deci o valoare, nu doua.
 *
 * SORTAREA SI PAGINAREA sunt in browser, ca in portal: acolo DataTables citea
 * un tabel deja desenat de php, cu toate randurile in el.
 *
 * RANDUL SE DESFACE PE LOC, cu aceleasi patru file ca `action_tabs.php`.
 */

const TABLE_ID = 'catalogTable'

/**
 * Coloanele din `index.php`, in aceeasi ordine - plus "Ticket type", care in
 * portal nu era coloana, ci numai campul "Parent ticket type" din formular.
 */
function catalogColumns(canWrite: boolean, canDelete: boolean, scopeWord: (value: number) => string): TableColumn[] {
  const as = (row: Record<string, unknown>) => row as unknown as CatalogEntry

  return [
    {
      /* Sageata de deschidere. `DataTable` se leaga de `center.show_details`,
         exact ca `ExpandRow` din `general.js`. */
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 40,
      className: 'text-center',
      html: (row) =>
        '<center class="show_details color-dark-grey pointer font-16" data-id="'
        + as(row).ID + '"><i class="fas fa-plus-circle"></i></center>',
    },
    {
      index: 1,
      title: 'Service',
      orderable: true,
      visible: true,
      html: (row) => {
        const entry = as(row)
        const icon = entry.icon !== '' ? '<i class="fas ' + esc(entry.icon) + '"></i> ' : ''
        const note = entry.short_description !== ''
          ? '<br><span class="adm-muted">' + esc(entry.short_description) + '</span>'
          : ''
        return '<a href="/app/service_catalog/composition/' + entry.ID + '">'
          + icon + '<strong>' + esc(entry.name) + '</strong></a>' + note
      },
    },
    {
      index: 2,
      title: 'Kind',
      orderable: true,
      visible: true,
      html: (row) =>
        '<span class="adm-pill' + (as(row).kind === 4 ? ' adm-pill-running' : '') + '">'
        + (as(row).kind === 4 ? 'Business' : 'IT') + '</span>',
    },
    {
      index: 3,
      title: 'Customer',
      orderable: true,
      visible: true,
      html: (row) => esc(as(row).company_name),
    },
    {
      index: 4,
      title: 'Owner',
      orderable: true,
      visible: true,
      html: (row) =>
        as(row).owner_name !== ''
          ? esc(as(row).owner_name)
          : '<span class="adm-pill adm-pill-warn">nobody</span>',
    },
    {
      index: 5,
      title: 'Rests on',
      orderable: true,
      visible: true,
      className: 'adm-numeric',
      html: (row) =>
        as(row).out_relation_count === 0
          ? '<span class="adm-pill adm-pill-bad">0</span>'
          : String(as(row).out_relation_count),
    },
    {
      index: 6,
      title: 'Published',
      orderable: true,
      visible: true,
      html: (row) => {
        const entry = as(row)
        const scope = entry.published === 0
          ? '<span class="adm-pill">no</span>'
          : '<span class="adm-pill adm-pill-ok">' + esc(scopeWord(entry.publish_scope)) + '</span>'
        const approval = entry.requires_approval === 1
          ? ' <span class="adm-pill adm-pill-warn" title="Asking for it needs an approval">'
            + '<i class="fas fa-user-check"></i></span>'
          : ''
        return scope + approval
      },
    },
    {
      /*
       * Tipul de tichet pe care il deschide o cerere din catalog -
       * `request_ticket_type_id`, "Parent ticket type" in formular. Fara el,
       * cum spune formularul, "it cannot be requested": se vede din rand, in
       * galben, nu abia cand cineva cere serviciul. Un id ramas fara tip (tipul
       * a fost sters) e mai rau - pare pus, dar nu deschide nimic -, deci rosu.
       */
      index: 7,
      title: 'Ticket type',
      orderable: true,
      visible: true,
      html: (row) => ticketTypeCell(as(row)),
    },
    {
      index: 8,
      title: '',
      orderable: false,
      visible: true,
      className: 'text-right adm-nowrap',
      html: (row) => {
        const entry = as(row)
        /* `<a>` pentru ce DESCHIDE o pagina, `<button>` pentru ce lucreaza pe
           loc: asa merge si click-mijloc pe primele doua, ca in portal. Si tot
           ca in portal, cele doua se deschid in FILA NOUA - omul se uita la
           lista si vrea sa vada un serviciu pe langa ea, nu in locul ei. */
        let html = '<a class="btn btn-info btn-xs" title="What it rests on"'
          + ' target="_blank" rel="noopener"'
          + ' href="/app/service_catalog/composition/' + entry.ID + '">'
          + '<i class="fas fa-sitemap"></i></a>'
          + '<a class="btn btn-default btn-xs" title="What other services it depends on"'
          + ' target="_blank" rel="noopener"'
          + ' href="/app/service_catalog/dependencies/' + entry.ID + '">'
          + '<i class="fas fa-code-branch"></i></a>'
          + '<button type="button" class="btn btn-default btn-xs catalogCosts" title="What it costs"'
          + ' data-id="' + entry.ID + '"><i class="fas fa-coins"></i></button>'
        if (canWrite) {
          html += '<button type="button" class="btn btn-default btn-xs catalogEdit" title="Edit"'
            + ' data-id="' + entry.ID + '"><i class="fas fa-pen"></i></button>'
        }
        if (canDelete) {
          html += '<button type="button" class="btn btn-danger btn-xs catalogRemove"'
            + ' title="Removes the catalogue entry, not the service"'
            + ' data-id="' + entry.ID + '"><i class="fas fa-times"></i></button>'
        }
        return html
      },
    },
  ]
}

/** Celula "Ticket type": numele, sau semnul ca lipseste. */
export function ticketTypeCell(entry: CatalogEntry): string {
  const name = entry.ticket_type_name ?? ''
  if (name !== '') return esc(name)
  if (entry.request_ticket_type_id > 0) {
    return '<span class="adm-pill adm-pill-bad" title="The ticket type set on it no longer exists;'
      + ' asking for it opens nothing">type #' + entry.request_ticket_type_id + ' missing</span>'
  }
  return '<span class="adm-pill adm-pill-warn" title="Asking for it opens no ticket">No ticket type</span>'
}

/** Cheia de sortare a fiecarei coloane - ce ordoneaza DataTables in portal. */
const SORT_KEYS: Record<number, (entry: CatalogEntry) => string | number> = {
  1: (e) => e.name.toLowerCase(),
  2: (e) => e.kind,
  3: (e) => e.company_name.toLowerCase(),
  4: (e) => e.owner_name.toLowerCase(),
  5: (e) => e.out_relation_count,
  6: (e) => (e.published === 0 ? 0 : e.publish_scope),
  /* Cele fara tip (si cele cu tipul sters) vin primele la crescator. */
  7: (e) => (e.ticket_type_name ?? '').toLowerCase(),
}

export function ServiceCatalogPage() {
  const [published, setPublished] = useState('')
  const [kind, setKind] = useState(0)
  const [ticketType, setTicketType] = useState('')
  const [form, setForm] = useState<number | null>(null)
  const [costs, setCosts] = useState(0)

  /* Cincizeci pe pagina si sortarea pe nume, ca in `service_catalog.js`: un
     catalog obisnuit are cateva zeci de servicii, iar intrebarea pusa aici e
     "ce avem", nu "unde e serviciul X" - la douazeci si cinci raspunsul
     incepea pe pagina a doua. `useTableState` tine minte alegerea, ca
     `stateSave`. */
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 50,
    order: { column: 1, dir: 'asc' },
    search: '',
  })

  const page = useQuery({
    ...serviceCatalogQuery({ published, kind, search: state.search, ticketType }),
    placeholderData: keepPreviousData,
  })
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(
    ['css/adm.css', 'css/service_shop.css', 'css/service_catalog.css'],
    shell.data?.version ?? '',
  )

  const client = useQueryClient()
  const remove = useMutation({
    mutationFn: (id: number) => removeCatalogEntry(id),
    onSuccess: (out) => {
      toast(out.message, out.status === 'OK' ? 'Removed' : 'Error', out.status === 'OK' ? 'success' : 'error')
      if (out.status === 'OK') void client.invalidateQueries({ queryKey: ['service-catalog'] })
    },
  })

  const entries = useMemo(() => page.data?.entries ?? [], [page.data])
  const scopes = page.data?.scopes ?? []
  const scopeWord = (value: number) => scopes.find((one) => one.value === value)?.label ?? 'yes'

  const sorted = useMemo(() => {
    const key = SORT_KEYS[state.order.column]
    if (!key) return entries
    const sign = state.order.dir === 'asc' ? 1 : -1
    return [...entries].sort((a, b) => {
      const left = key(a)
      const right = key(b)
      if (typeof left === 'string' || typeof right === 'string') {
        return sign * String(left).localeCompare(String(right))
      }
      return sign * (left - right)
    })
  }, [entries, state.order.column, state.order.dir])

  const canWrite = page.data?.canWrite ?? false
  const canDelete = page.data?.canDelete ?? false
  const columns = useMemo(
    () => catalogColumns(canWrite, canDelete, scopeWord),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, canDelete, scopes],
  )

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  const { counts } = page.data
  const shown = sorted.slice(state.start, state.start + state.length)

  /*
   * Butoanele de pe rand sunt HTML scris de coloana, deci n-au `onClick` al
   * lor: clicul se prinde sus, pe delegare, cum il prindea jQuery in
   * `service_catalog.js`.
   */
  const onRowClick = (event: MouseEvent<HTMLDivElement>) => {
    const button = (event.target as HTMLElement).closest('button')
    if (!button) return
    const id = Number(button.getAttribute('data-id') ?? 0)
    if (id < 1) return
    if (button.classList.contains('catalogCosts')) setCosts(id)
    if (button.classList.contains('catalogEdit')) setForm(id)
    if (button.classList.contains('catalogRemove')) {
      /* Spus intreg: "remove" langa un serviciu se citeste ca stergerea
         serviciului, si nu asta face. Acelasi text ca in portal. */
      if (
        !window.confirm(
          'Remove this entry from the catalogue?\n\nThe service itself, its relations and its history stay '
            + 'exactly as they are.',
        )
      ) {
        return
      }
      remove.mutate(id)
    }
  }

  return (
    <div id="content">
      <CatalogHead
        title="Service catalog"
        icon="fa-concierge-bell"
        tone="blue-mauve"
        note="the services the business names, what each one rests on, and what it costs"
        stats={[
          { value: counts.services, label: 'services', tone: 'blue-mauve' },
          { value: counts.unpublished, label: 'unpublished', tone: counts.unpublished > 0 ? 'grey' : 'green-mode' },
          { value: counts.ownerless, label: 'no owner', tone: counts.ownerless > 0 ? 'yellow' : 'green-mode' },
          { value: counts.empty, label: 'rest on nothing', tone: counts.empty > 0 ? 'red-intense' : 'green-mode' },
        ]}
      />

      <CatalogTabs active="catalog" />

      {(counts.ownerless > 0 || counts.empty > 0) && (
        <div className="adm-card adm-tone-yellow">
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-exclamation-circle"></i> Worth finishing
            </h5>
          </div>
          {counts.empty > 0 && (
            <p className="adm-policy">
              <strong>{counts.empty}</strong> service{counts.empty === 1 ? '' : 's'} rest
              {counts.empty === 1 ? 's' : ''} on nothing. Until something is attached, the impact report cannot say what
              breaks when it does, and the map draws it alone.
            </p>
          )}
          {counts.ownerless > 0 && (
            <p className="adm-policy">
              <strong>{counts.ownerless}</strong> ha{counts.ownerless === 1 ? 's' : 've'} no owner. That is the most
              common reason a catalogue rots — nobody is asked when the service changes. The owner is set on the item
              itself, in CMDB.
            </p>
          )}
        </div>
      )}

      <div onClick={onRowClick}>
        <DataTable
          id={TABLE_ID}
          columnTools
          columns={columns}
          rows={shown as unknown as Record<string, unknown>[]}
          total={sorted.length}
          fetching={page.isFetching}
          error={page.error ? page.error.message : null}
          onRetry={() => void page.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => `catalog_${(row as unknown as CatalogEntry).ID}`}
          searchHint="name or description"
          emptyHint="No service matches."
          details={(row) => <RowDetails entryId={(row as unknown as CatalogEntry).ID} canWrite={canWrite} />}
          headerButtons={
            canWrite ? (
              <button
                type="button"
                className="btn btn-success tk-header-extra"
                id="catalogAdd"
                onClick={() => setForm(0)}
              >
                <i className="fas fa-plus"></i> Add
              </button>
            ) : undefined
          }
          filters={{
            active: [kind, published !== '' ? 1 : 0, ticketType !== '' ? 1 : 0].filter(Boolean).length,
            render: (close) => (
              <div className="adm-bar" id="catalogFilters">
                <div className="adm-field">
                  <label htmlFor="catKind">Kind</label>
                  <select
                    className="form-control input-sm"
                    id="catKind"
                    value={kind}
                    onChange={(e) => {
                      setKind(Number(e.target.value))
                      setState({ start: 0 })
                    }}
                  >
                    <option value={0}>Everything</option>
                    <option value={4}>Business services</option>
                    <option value={3}>IT services</option>
                  </select>
                </div>

                <div className="adm-field">
                  <label htmlFor="catPublished">Published</label>
                  <select
                    className="form-control input-sm"
                    id="catPublished"
                    value={published}
                    onChange={(e) => {
                      setPublished(e.target.value)
                      setState({ start: 0 })
                    }}
                  >
                    <option value="">Either</option>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>

                <div className="adm-field">
                  <label htmlFor="catTicketTypeFilter">Ticket type</label>
                  <select
                    className="form-control input-sm"
                    id="catTicketTypeFilter"
                    value={ticketType}
                    onChange={(e) => {
                      setTicketType(e.target.value)
                      setState({ start: 0 })
                    }}
                  >
                    <option value="">Either</option>
                    <option value="1">Set</option>
                    <option value="0">Missing</option>
                  </select>
                </div>

                {/*
                  Cautarea NU se scrie si aici. In portal bara asta avea si un
                  camp "Search" fiindca al lui DataTables cauta in randurile
                  deja desenate, iar asta trimitea intrebarea la server. Aici
                  cel din antet E cel al serverului, deci un al doilea ar fi
                  fost acelasi lucru de doua ori.
                */}
                <span className="adm-spacer"></span>

                <button
                  type="button"
                  className="btn btn-default btn-sm closeFilters"
                  style={{ marginBottom: 2 }}
                  onClick={close}
                >
                  Close
                </button>
              </div>
            ),
          }}
          footerButtons={{ copy: true, colvis: true, refresh: () => void page.refetch() }}
        />
      </div>

      <div id="admWarning" className="adm-warning"></div>

      <Modal
        id="catalogModal"
        open={form !== null}
        onClose={() => setForm(null)}
        title={
          <>
            <i className="fas fa-concierge-bell"></i>{' '}
            {form ? entries.find((one) => one.ID === form)?.name ?? 'Service' : 'Add a service to the catalogue'}
          </>
        }
      >
        {form !== null && (
          <ServiceCatalogForm id={form} onSaved={() => setForm(null)} onClose={() => setForm(null)} />
        )}
      </Modal>

      <Modal
        id="catalogCostsModal"
        open={costs > 0}
        onClose={() => setCosts(0)}
        title={
          <>
            <i className="fas fa-coins"></i> What {entries.find((one) => one.ID === costs)?.name ?? 'it'} costs
          </>
        }
        footer={
          <button type="button" className="btn btn-default" onClick={() => setCosts(0)}>
            Close
          </button>
        }
      >
        {costs > 0 && (
          <div className="modal-body">
            <CostsTab entryId={costs} />
          </div>
        )}
      </Modal>
    </div>
  )
}

/** Panoul unui rand deschis: filele lui, si care e deschisa. */
function RowDetails({ entryId, canWrite }: { entryId: number; canWrite: boolean }) {
  const [tab, setTab] = useState<CatalogRowTab>('composition')

  return <CatalogRowTabs entryId={entryId} canWrite={canWrite} tab={tab} onTab={setTab} />
}

/** Filele randului desfacut - `action_tabs.php`. */
export type CatalogRowTab = 'composition' | 'dependencies' | 'costs' | 'edit'

/**
 * Meniul dintr-un rand deschis - `views/version-3/inventoree/service_catalog/
 * action_tabs.php`.
 *
 * PATRU FILE, nu trei. Macheta veche le scrie pe rand: "Rests on", "Depends
 * on", "Costs" si, cand omul are dreptul de scriere, "Edit" - formularul
 * deschis CHIAR ACOLO, nu intr-o fereastra. La mutare a patra lipsea, si
 * odata cu ea singurul loc din care se poate schimba descrierea unui serviciu
 * fara sa se piarda randul deschis.
 *
 * "Remove" nu e fila nici in portal: sta pe rand, in tabel, si o singura cale
 * catre o stergere e mai bine decat doua.
 */
export function CatalogRowTabs({ entryId, canWrite, tab, onTab }: {
  entryId: number
  canWrite: boolean
  tab: CatalogRowTab
  onTab(next: CatalogRowTab): void
}) {
  const client = useQueryClient()
  const tabs: [CatalogRowTab, string][] = [
    ['composition', 'Rests on'],
    ['dependencies', 'Depends on'],
    ['costs', 'Costs'],
  ]
  if (canWrite) tabs.push(['edit', 'Edit'])

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.map(([name, label]) => (
            <li key={name}>
              <a className={`action_button${tab === name ? ' active' : ''}`} onClick={() => onTab(name)}>
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'composition' && <CompositionTab entryId={entryId} />}
          {tab === 'dependencies' && <DependenciesTab entryId={entryId} />}
          {tab === 'costs' && <CostsTab entryId={entryId} />}
          {tab === 'edit' && (
            /*
             * Acelasi formular ca in fereastra si ca la `/service_catalog/form/
             * {id}` - nu o a doua copie a lui. In portal `form()` intorcea chiar
             * bucata ferestrei, iar `ExpandRow` o punea in `.details_content`.
             */
            <div className="adm-card adm-tone-blue-mauve">
              <ServiceCatalogForm
                id={entryId}
                onSaved={() => {
                  void client.invalidateQueries({ queryKey: ['service-catalog'] })
                  onTab('composition')
                }}
                onClose={() => onTab('composition')}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
